import * as Sentry from '@sentry/react';
import type { Request as SentryRequest } from '@sentry/react';
import { AjaxError } from 'rxjs-compat';

interface SentryReportAPIType {
  xhr: XMLHttpRequest;
  request: {
    url?: string;
    body?: unknown;
    responseURL?: string;
    gtm?: {
      label: string;
    };
    transformBodyForSentry?: () => unknown;
  };
  status: string;
}

interface SentryReportGenericType {
  message: string;
}

const api = (error: SentryReportAPIType | AjaxError) => {
  const request = error.request || {};
  const status = error.status || '';
  const {
    url = '',
    responseURL = '',
    gtm,
    transformBodyForSentry,
  } = request as SentryReportAPIType['request'];
  const message = `[API] ${status} ${gtm?.label || url || responseURL}`;

  if (transformBodyForSentry) {
    request.body = transformBodyForSentry();
  }

  const correlationId = error.xhr?.getResponseHeader('x-correlation-id');
  Sentry.withScope(scope => {
    if (correlationId) {
      scope.setTag('correlationId', correlationId);
    }
    scope.captureEvent({
      message,
      logger: 'api',
      extra: {
        error,
      },
    });
  });
};

const ui = (error: SentryReportGenericType) => {
  const { message } = error || {};

  Sentry.captureEvent({
    message: `[UI] ${message}`,
    logger: 'ui',
    extra: {
      error,
    },
  });
};

const rxjs = (error: SentryReportGenericType) => {
  const { message } = error;

  Sentry.captureEvent({
    message: `[RXJS] ${message}`,
    logger: 'rxjs',
    extra: {
      error,
    },
  });
};

const feedback = (title: string, feedback: Record<string, unknown>) => {
  title = `[F] ${title}`;

  if (title.length > 200) {
    title = `${title.substring(0, 200)}...`;
  }

  Sentry.captureEvent({
    message: title,
    logger: 'feedback',
    level: 'info',
    extra: {
      ...feedback,
    },
  });
};

const info = (data: SentryReportGenericType) => {
  const { message } = data || {};

  Sentry.captureEvent({
    message: `[INFO] ${message}`,
    logger: 'debug',
    extra: {
      data,
    },
  });
};

export function sentryRTKQueryReport(params: {
  endpointName: string;
  statusCode: number;
  httpMethod: string;
  transactionName: string;
  esbErrorCode?: string;
  webscriptResponseCode?: string;
  correlationId?: string;
  request: Request;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  extra: any;
}) {
  Sentry.withScope(scope => {
    if (params.correlationId) {
      scope.setTag('correlationId', params.correlationId || '');
    }
    if (params.esbErrorCode) {
      scope.setTag('esbErrorCode', params.esbErrorCode || '');
    }

    scope.setTransactionName(params.transactionName || '');
    scope.setTag('endpoint', params.endpointName || '');
    scope.setTag('status', params.statusCode || '');
    if (params.webscriptResponseCode) {
      scope.setTag('ResponseCode', params.webscriptResponseCode);
    }
    scope.setTag('httpMethod', params.httpMethod || '');
    scope.captureEvent({
      message: `[RTKQ] ${params.endpointName} ${params.statusCode}`,
      level: 'info',
      logger: 'rtk',
      extra: params.extra,
      request: params.request as unknown as SentryRequest,
    });
  });
}

export default {
  api,
  ui,
  rxjs,
  feedback,
  info,
};
